// Bottom-right minimap: SVG overview of every shape on the board with an
// inset rectangle showing the current viewport. Click to recenter; drag the
// viewport rect to pan. Reads `viewport` + `containerSize` (screen-space) +
// `shapes` (world-space) and translates between them.
//
// The minimap fits the union of (all shapes ∪ current viewport bounds) so
// that scrolling off into empty space still keeps the viewport indicator on
// screen — otherwise the rect would clip off the edge as you panned away
// from the content.

import { useCallback, useMemo, useRef } from 'react';
import type { Shape } from '@frame/shared';
import { shapeBBox } from './binding/shapesMap';
import { unionBBox, type BBox, type Size, type Viewport } from './viewport';

interface Props {
  viewport: Viewport;
  setViewport: (next: Viewport | ((prev: Viewport) => Viewport)) => void;
  containerSize: Size;
  shapes: readonly Shape[];
}

const MAP_W = 180;
const MAP_H = 120;
const MAP_PAD = 8;

export function Minimap({ viewport, setViewport, containerSize, shapes }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ startMap: { x: number; y: number }; startViewport: Viewport } | null>(
    null,
  );

  const shapeBoxes = useMemo<BBox[]>(() => {
    const out: BBox[] = [];
    for (const s of shapes) {
      const b = shapeBBox(s);
      if (b) out.push(b);
    }
    return out;
  }, [shapes]);

  // The visible viewport in world coords — derived live so the viewport rect
  // stays exactly aligned with what the user sees.
  const viewWorld = useMemo<BBox>(() => {
    if (containerSize.width === 0 || containerSize.height === 0 || viewport.scale === 0) {
      return { x: 0, y: 0, w: 0, h: 0 };
    }
    return {
      x: -viewport.x / viewport.scale,
      y: -viewport.y / viewport.scale,
      w: containerSize.width / viewport.scale,
      h: containerSize.height / viewport.scale,
    };
  }, [viewport, containerSize.width, containerSize.height]);

  const worldBBox = useMemo<BBox>(() => {
    const union = unionBBox([...shapeBoxes, viewWorld]);
    if (!union || (union.w === 0 && union.h === 0)) {
      // Empty board: center on the viewport with a fixed window so we have
      // a sensible coordinate space to draw the viewport rect in.
      return {
        x: viewWorld.x - viewWorld.w / 4,
        y: viewWorld.y - viewWorld.h / 4,
        w: Math.max(1, viewWorld.w * 1.5),
        h: Math.max(1, viewWorld.h * 1.5),
      };
    }
    return union;
  }, [shapeBoxes, viewWorld]);

  // World → minimap pixel projection. Padding shrinks the drawable area so
  // shapes never touch the chrome edge.
  const innerW = MAP_W - MAP_PAD * 2;
  const innerH = MAP_H - MAP_PAD * 2;
  const projScale = Math.min(innerW / Math.max(1, worldBBox.w), innerH / Math.max(1, worldBBox.h));
  const projOffsetX = MAP_PAD + (innerW - worldBBox.w * projScale) / 2 - worldBBox.x * projScale;
  const projOffsetY = MAP_PAD + (innerH - worldBBox.h * projScale) / 2 - worldBBox.y * projScale;

  const toMap = (b: BBox) => ({
    x: b.x * projScale + projOffsetX,
    y: b.y * projScale + projOffsetY,
    w: Math.max(1, b.w * projScale),
    h: Math.max(1, b.h * projScale),
  });

  const viewportRect = toMap(viewWorld);

  // Convert a click on the minimap into a viewport that re-centers the
  // canvas on the corresponding world point.
  const centerOnMapPoint = useCallback(
    (mapPt: { x: number; y: number }) => {
      const worldX = (mapPt.x - projOffsetX) / projScale;
      const worldY = (mapPt.y - projOffsetY) / projScale;
      setViewport((v) => ({
        scale: v.scale,
        x: containerSize.width / 2 - worldX * v.scale,
        y: containerSize.height / 2 - worldY * v.scale,
      }));
    },
    [projOffsetX, projOffsetY, projScale, setViewport, containerSize.width, containerSize.height],
  );

  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    e.preventDefault();
    const rect = svg.getBoundingClientRect();
    const mapPt = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    const insideViewport =
      mapPt.x >= viewportRect.x &&
      mapPt.x <= viewportRect.x + viewportRect.w &&
      mapPt.y >= viewportRect.y &&
      mapPt.y <= viewportRect.y + viewportRect.h;

    if (!insideViewport) {
      // Click outside the viewport rect: jump-recenter.
      centerOnMapPoint(mapPt);
    }
    dragRef.current = { startMap: mapPt, startViewport: viewport };

    const onMove = (ev: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = ev.clientX - rect.left - drag.startMap.x;
      const dy = ev.clientY - rect.top - drag.startMap.y;
      // Map-pixel delta → world delta → screen delta against the starting
      // viewport. We snapshot the starting viewport so the drag feels stable
      // even as setViewport schedules updates async.
      const dWorldX = dx / projScale;
      const dWorldY = dy / projScale;
      const startScale = drag.startViewport.scale;
      setViewport({
        scale: startScale,
        x: drag.startViewport.x - dWorldX * startScale,
        y: drag.startViewport.y - dWorldY * startScale,
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      aria-label="Board minimap"
      className="
        absolute right-3 bottom-14 z-[3]
        bg-bg-panel/95 backdrop-blur
        border border-border-default rounded-2
        shadow-sm
        overflow-hidden
      "
      style={{ width: MAP_W, height: MAP_H }}
    >
      <svg
        ref={svgRef}
        width={MAP_W}
        height={MAP_H}
        onMouseDown={onMouseDown}
        style={{ display: 'block', cursor: 'pointer' }}
        role="presentation"
      >
        {shapeBoxes.map((b, i) => {
          const r = toMap(b);
          return (
            <rect
              key={i}
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              fill="var(--fg-quaternary)"
              opacity={0.5}
              rx={1}
            />
          );
        })}
        <rect
          x={viewportRect.x}
          y={viewportRect.y}
          width={viewportRect.w}
          height={viewportRect.h}
          fill="var(--accent)"
          fillOpacity={0.1}
          stroke="var(--accent)"
          strokeWidth={1.25}
          rx={2}
          style={{ cursor: 'grab' }}
        />
      </svg>
    </div>
  );
}
